import React, { forwardRef, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { tokens } from "../../public/tokens";
import Badge from "./Badge";
import Checkbox from "./Checkbox";
import {
    getNextHighlightedIndexOnArrowKey,
    useDropdownPosition,
    useOutsideClick,
    useScrollHighlightedIntoView,
} from "../../hooks/useDropdownBehavior";
import {
    sizes,
    StyledInputGroup,
    StyledDefaultInput,
    StyledTop,
    StyledLabel,
    StyledRequired,
    StyledInfoIcon,
    StyledSelectWrapper,
    StyledDivider,
    StyledDropdownList,
    StyledStateItem,
    StyledMessage,
} from "./SelectDropdown.styles";

/*
 * Multi-select's tag row needs the value box to stretch to fit wrapped chips
 * (SelectDropdown's single-line value box only needs to center its text), so
 * this styled component stays local rather than sharing SelectDropdown's -
 * but the visual values (box-shadow, border) are kept identical to it.
 */
const StyledSelectValue = styled.div(({ $size = "lg", $disabled = false, $error = false, $open = false }) => {
    const size = sizes[$size] || sizes.lg;
    return {
        width: "100%",
        minHeight: size.height,
        padding: size.padding,
        paddingTop: tokens.globals["spacing-0-5"],
        paddingBottom: tokens.globals["spacing-0-5"],
        backgroundColor: $disabled ? tokens.colors.neutral.grey["100"] : tokens.colors.surfaces["container-background"],
        border: `${tokens.globals["spacing-px"]} solid ${$error ? tokens.colors.ui.destructive : ($open ? tokens.colors.ui.ring : tokens.colors.surfaces["border-divider"])}`,
        borderRadius: size.borderRadius,
        boxShadow: "none",
        display: "flex",
        alignItems: "stretch",
        gap: tokens.globals["spacing-1-5"],
        cursor: $disabled ? "not-allowed" : "pointer",
        opacity: $disabled ? 0.6 : 1,
        fontSize: size.fontSize,
        lineHeight: size.lineHeight,
        transition: "border-color 0.15s ease-in-out",

        "&:hover": $disabled ? {} : { borderColor: $open ? tokens.colors.ui.ring : tokens.colors.neutral.grey["500"] },
        "&:focus-visible": $disabled ? {} : {
            outline: `${tokens.globals["spacing-px"]} solid ${tokens.colors.ui.ring}`,
            outlineOffset: tokens.globals["spacing-px"],
        },
    };
});

const StyledTagsContainer = styled.div({
    flex: "1 1 auto",
    minWidth: 0,
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: tokens.globals["spacing-1"],
    padding: `${tokens.globals["spacing-0-5"]} 0`,
});

const StyledPlaceholderText = styled.span({
    color: tokens.colors.text.secondary,
    fontSize: tokens.typography.fontSize["lg-14px"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontWeight: tokens.typography.fontWeights.regular,
    lineHeight: tokens.typography.lineHeights["110"],
});

const StyledTag = styled.span({
    display: "inline-flex",
    alignItems: "center",
    gap: tokens.globals["spacing-1"],
    padding: `${tokens.globals["spacing-0-5"]} ${tokens.globals["spacing-2"]}`,
    background: tokens.colors.brand.blue["100"],
    color: tokens.colors.brand["aie-blue"]["500"],
    borderRadius: tokens.globals["border-radius"],
    fontSize: tokens.typography.fontSize["md-12px"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontWeight: tokens.typography.fontWeights.medium,
    lineHeight: "16px",
});

const StyledColorDot = styled.span(({ $color }) => ({
    width: "8px",
    height: "8px",
    flexShrink: 0,
    borderRadius: "50%",
    background: $color,
}));

const StyledTagRemove = styled.button({
    width: "18px",
    height: "18px",
    padding: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: "none",
    background: "transparent",
    color: "currentColor",
    borderRadius: tokens.globals["border-radius"],
    cursor: "pointer",

    "&:hover": { background: tokens.colors.brand.blue["200"] },
    "&:focus-visible": {
        outline: `${tokens.globals["spacing-px"]} solid ${tokens.colors.brand.blue["500"]}`,
        outlineOffset: tokens.globals["spacing-px"],
    },
});

const StyledDropdown = styled.span({
    minWidth: tokens.globals["spacing-8"],

    height: tokens.globals["spacing-7"],
    alignSelf: "center",
    marginLeft: "auto",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: tokens.globals["spacing-2-5"],
    flexShrink: 0,
});

const StyledChevron = styled.i(({ $open = false }) => ({
    color: tokens.colors.text.primary,
    fontSize: tokens.typography.fontSize["base-16px"],
    lineHeight: "16px",
    transform: $open ? "rotate(180deg)" : "rotate(0deg)",
    transition: "transform 0.2s ease-in-out",
}));

/*
 * Kept local rather than sharing SelectDropdown's StyledDropdownItem: this one adds a `gap`
 * for the "filter" variant's checkbox - but the highlighted/hover background is kept at the
 * same tenant `hover-color` SelectDropdown uses, not a separate blue tint, and hover doesn't
 * change the label color either, matching single-select. `$selected` only ever reaches here
 * true for the "filter" variant now, since the "default" variant's list excludes selected
 * options.
 */
const StyledDropdownItem = styled.li(({ $selected = false, $highlighted = false, $disabled = false }) => ({
    padding: `${tokens.globals["spacing-sm"]} ${tokens.globals["spacing-3"]}`,
    display: "flex",
    alignItems: "center",
    gap: tokens.globals["spacing-sm"],
    cursor: $disabled ? "not-allowed" : "pointer",
    fontSize: tokens.typography.fontSize["lg-14px"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontWeight: tokens.typography.fontWeights.regular,
    color: $disabled
        ? tokens.colors.text.disabled
        : ($selected ? tokens.colors.brand.blue["500"] : tokens.colors.text.primary),
    background: $highlighted && !$disabled ? tokens.colors.theme["hover-color"] : "transparent",
    opacity: $disabled ? 0.6 : 1,

    "&:hover": $disabled ? {} : {
        background: tokens.colors.theme["hover-color"],
    },
}));

/*
 * The "filter" variant's closed trigger - a fixed, shrink-to-content button (unlike the
 * default variant's `width: 100%` field) so selecting more options never grows the toolbar.
 * Matches FilterBar's StyledFilterButton look, plus an $active state once something's picked.
 */
const StyledFilterTrigger = styled.button(({ $active = false, $open = false, $disabled = false }) => ({
    height: "35px",
    padding: `0 ${tokens.globals["spacing-2-5"]}`,
    background: $active ? tokens.colors.brand.blue["50"] : tokens.colors.surfaces["container-background"],
    boxShadow: tokens.effects.effects.shadow.default,
    borderRadius: tokens.globals["border-radius"],
    border: `${tokens.globals["spacing-px"]} solid ${$active || $open ? tokens.colors.brand.blue["500"] : tokens.colors.surfaces["border-divider"]}`,
    display: "inline-flex",
    alignItems: "center",
    gap: tokens.globals["spacing-1-5"],
    cursor: $disabled ? "not-allowed" : "pointer",
    opacity: $disabled ? 0.6 : 1,
    color: $active ? tokens.colors.brand.blue["500"] : tokens.colors.text.secondary,
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontSize: tokens.typography.fontSize["lg-14px"],
    fontWeight: tokens.typography.fontWeights.regular,
    whiteSpace: "nowrap",
    transition: "border-color 0.15s ease-in-out, background 0.15s ease-in-out",

    "&:hover": $disabled ? {} : { borderColor: tokens.colors.brand.blue["500"] },
    "&:focus-visible": {
        outline: `${tokens.globals["spacing-px"]} solid ${tokens.colors.brand.blue["500"]}`,
        outlineOffset: tokens.globals["spacing-0-5"],
    },
}));

/**
 * The dropdown item's Checkbox is a controlled visual indicator only - selecting an option is
 * driven by the item's own onClick (matching every other variant), so the checkbox itself is
 * taken out of the hit-test/tab order rather than acting as its own (redundant) click target.
 */
const StyledOptionCheckbox = styled.span({
    pointerEvents: "none",
    display: "inline-flex",
});

/**
 * Standard AIE multi-select dropdown, portalled to `document.body`.
 * @param {Array<object>} [props.options] `{ value, label, disabled?, color? }`; `color` renders a dot.
 * @param {"default"|"filter"} [props.variant="default"] "filter" is a fixed-width button + count
 *   badge instead of growing chips, for table toolbars (see AssignModulesModal's Status filter).
 * @param {Array} [props.value] Controlled selected values.
 * @param {Function} [props.onChange] Called with `{ target: { value, name } }`, the full next selection.
 * Also: onSelect, onOpenChange, placeholder, size, disabled, loading, error, label, required, info, helperText, errorText, name, id, ariaLabel, className, disabledReason, loadingMessage, emptyMessage (self-explanatory).
 */
const MultiSelectDropdown = forwardRef(function MultiSelectDropdown(
    {
        options = [],
        placeholder = "Select items",
        variant = "default",
        value = [],
        onChange,
        onSelect,
        onOpenChange,
        size = "lg",
        disabled = false,
        disabledReason,
        loading = false,
        loadingMessage = "Loading options...",
        emptyMessage = "No options available",
        error = false,
        label,
        required = false,
        info,
        helperText,
        errorText,
        name = "",
        id,
        ariaLabel,
        className,
        ...props
    },
    ref
) {
    const [isOpen, setIsOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const containerRef = useRef(null);
    const listRef = useRef(null);
    const selectedValues = Array.isArray(value) ? value : [];
    const isSelected = (option) => selectedValues.some((item) => String(item) === String(option.value));
    const selectedOptions = options.filter(isSelected);
    const listOptions = variant === "filter" ? options : options.filter((option) => !isSelected(option));
    const showError = Boolean(errorText || error);
    const listboxId = id ? `${id}-listbox` : undefined;
    const message = errorText || helperText || (disabled ? disabledReason : undefined);

    const setOpen = (nextOpen) => {
        setIsOpen(nextOpen);
        setHighlightedIndex(-1);
        if (onOpenChange) {
            onOpenChange(nextOpen);
        }
    };

    const emitChange = (nextValue) => {
        const target = { value: nextValue, name };
        if (onChange) {
            onChange({ target, currentTarget: target });
        }
    };

    const handleSelect = (option) => {
        if (disabled || (option && option.disabled)) {
            return;
        }

        const nextValue = selectedValues.some((item) => String(item) === String(option.value))
            ? selectedValues.filter((item) => String(item) !== String(option.value))
            : [...selectedValues, option.value];

        emitChange(nextValue);

        if (onSelect) {
            onSelect(option, nextValue);
        }
    };

    const handleRemoveTag = (optionValue, event) => {
        event.preventDefault();
        event.stopPropagation();
        if (disabled) {
            return;
        }
        emitChange(selectedValues.filter((item) => item !== optionValue));
    };

    const handleKeyDown = (event) => {
        if (disabled) {
            return;
        }

        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            if (isOpen && highlightedIndex >= 0 && listOptions[highlightedIndex]) {
                handleSelect(listOptions[highlightedIndex]);
            } else {
                setOpen(!isOpen);
            }
            return;
        }

        if (event.key === "Escape") {
            setOpen(false);
            return;
        }

        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            if (!isOpen) {
                setOpen(true);
            }
            setHighlightedIndex((current) => getNextHighlightedIndexOnArrowKey(listOptions, current, event.key));
        }
    };

    useOutsideClick(containerRef, listRef, isOpen, () => setOpen(false));

    const menuPosition = useDropdownPosition(containerRef, isOpen);

    useScrollHighlightedIntoView(listRef, isOpen, highlightedIndex);

    useEffect(() => {
        if (highlightedIndex >= listOptions.length) {
            setHighlightedIndex(-1);
        }
    }, [listOptions, highlightedIndex]);

    const autoWidth = variant === "filter";

    return (
        <StyledInputGroup className={className} $autoWidth={autoWidth}>
            <StyledDefaultInput $autoWidth={autoWidth}>
                {label && (
                    <StyledTop>
                        <StyledLabel htmlFor={id}>
                            {label}
                            {required && <StyledRequired aria-hidden="true">*</StyledRequired>}
                        </StyledLabel>
                        {info && <StyledInfoIcon className="fal fa-info-circle" title={info} aria-label={info} />}
                    </StyledTop>
                )}

                <StyledSelectWrapper ref={containerRef} $autoWidth={autoWidth}>
                    {variant === "filter" ? (
                        <StyledFilterTrigger
                            {...props}
                            ref={ref}
                            type="button"
                            id={id}
                            $active={selectedValues.length > 0}
                            $open={isOpen}
                            $disabled={disabled}
                            disabled={disabled}
                            role="combobox"
                            aria-label={ariaLabel || label || placeholder}
                            aria-haspopup="listbox"
                            aria-expanded={isOpen}
                            aria-controls={listboxId}
                            title={disabled ? disabledReason : undefined}
                            onClick={() => !disabled && setOpen(!isOpen)}
                            onKeyDown={handleKeyDown}
                        >
                            <span>{placeholder}</span>
                            {selectedValues.length > 0 && <Badge tone="blue">{selectedValues.length}</Badge>}
                            <StyledChevron $open={isOpen} className="fal fa-chevron-down" />
                        </StyledFilterTrigger>
                    ) : (
                        <StyledSelectValue
                            {...props}
                            ref={ref}
                            id={id}
                            $size={size}
                            $disabled={disabled}
                            $error={showError}
                            $open={isOpen}
                            role="combobox"
                            tabIndex={disabled ? -1 : 0}
                            aria-label={ariaLabel || label || placeholder}
                            aria-haspopup="listbox"
                            aria-expanded={isOpen}
                            aria-controls={listboxId}
                            aria-disabled={disabled || undefined}
                            aria-invalid={showError || undefined}
                            title={disabled ? disabledReason : undefined}
                            onClick={() => !disabled && setOpen(!isOpen)}
                            onKeyDown={handleKeyDown}
                        >
                            <StyledTagsContainer>
                                {selectedOptions.length > 0 ? selectedOptions.map((option) => (
                                    <StyledTag key={option.value}>
                                        {option.color && <StyledColorDot $color={option.color} aria-hidden="true" />}
                                        {option.label}
                                        {!disabled && (
                                            <StyledTagRemove
                                                type="button"
                                                onClick={(event) => handleRemoveTag(option.value, event)}
                                                aria-label={`Remove ${option.label}`}
                                            >
                                                <i className="fal fa-times" aria-hidden="true" />
                                            </StyledTagRemove>
                                        )}
                                    </StyledTag>
                                )) : <StyledPlaceholderText>{placeholder}</StyledPlaceholderText>}
                            </StyledTagsContainer>

                            <StyledDropdown aria-hidden="true">
                                <StyledDivider />
                                <StyledChevron $open={isOpen} className="fal fa-chevron-down" />
                            </StyledDropdown>
                        </StyledSelectValue>
                    )}

                    {isOpen && createPortal(
                        <StyledDropdownList
                            id={listboxId}
                            ref={listRef}
                            role="listbox"
                            aria-multiselectable="true"
                            $size={size}
                            $top={menuPosition.top}
                            $left={menuPosition.left}
                            $width={menuPosition.width}
                            $autoWidth={autoWidth}
                        >
                            {loading ? (
                                <StyledStateItem role="status">{loadingMessage}</StyledStateItem>
                            ) : listOptions.length === 0 ? (
                                <StyledStateItem>{emptyMessage}</StyledStateItem>
                            ) : (
                                listOptions.map((option, index) => {
                                    const selected = isSelected(option);
                                    return (
                                        <StyledDropdownItem
                                            key={option.value}
                                            role="option"
                                            aria-selected={selected}
                                            aria-disabled={option.disabled || undefined}
                                            $selected={selected}
                                            $highlighted={index === highlightedIndex}
                                            $disabled={Boolean(option.disabled)}
                                            title={option.disabled ? option.disabledReason : option.title}
                                            onClick={() => handleSelect(option)}
                                            onMouseEnter={() => !option.disabled && setHighlightedIndex(index)}
                                        >
                                            {variant === "filter" && (
                                                <StyledOptionCheckbox aria-hidden="true">
                                                    <Checkbox
                                                        checked={selected}
                                                        disabled={option.disabled}
                                                        onChange={() => {}}
                                                        tabIndex={-1}
                                                        size="sm"
                                                    />
                                                </StyledOptionCheckbox>
                                            )}
                                            {option.color && <StyledColorDot $color={option.color} aria-hidden="true" />}
                                            {option.label}
                                        </StyledDropdownItem>
                                    );
                                })
                            )}
                        </StyledDropdownList>,
                        document.body
                    )}
                </StyledSelectWrapper>

                {message && <StyledMessage $error={Boolean(errorText)}>{message}</StyledMessage>}
            </StyledDefaultInput>
        </StyledInputGroup>
    );
});

MultiSelectDropdown.displayName = "MultiSelectDropdown";

export default MultiSelectDropdown;
